import { memo, useCallback, useMemo, useState } from 'react';
import { components } from '@api/club/generated';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { debounce } from 'throttle-debounce';

import { sortBy } from '@libs/array';
import { ChessWinner } from '@libs/chess/types';
import { Tabs } from '@ui/components/Tabs';

import { useUserSocket } from '@features/shared/api';
import { useRoundShortGamesSocket } from '@features/shared/api/streams';
import {
  GameParticipantsResult,
  ResultsGame,
  ResultsTableParticipant,
} from '@features/shared/api/typings';
import {
  QUERY_KEYS,
  useRoundRobnResultsQuery,
  useSwissPrivateTeamResultsForTeamsQuery,
  useSwissResultsQuery,
  useTournamentResultsQuery,
} from '@features/tournament/api/hooks';
import { useGoToGame } from '@features/tournament/hooks/useGoToGame';
import { useTournamentCtx } from '@features/tournament/providers/TournamentProvider';
import { EliminationBrackets } from '@features/tournament/ui/components/EliminationBrackets';
import {
  GamePreviewInitialData,
  GamePreviewWidget,
  useGamePreview,
} from '@features/tournament/ui/components/GamePreview';
import { PlayerShieldQuickPeekWidget } from '@features/user/ui/PlayerShieldQuickPeek/PlayerShieldQuickPeekWidget';

import { ResultsTable } from './ResultsTable';
import { ResultsTableSkeleton } from './ResultsTableSkeleton';

import styles from './ResultsTable.module.css';

export type ResultsTableSort = 'place' | 'seed';

const REFETCH_DEBOUNCE_MS = 3 * 1000;

/*
    ____,-------------------------------,____
    \   |          Компонент            |   /
    /___|-------------------------------|___\
*/

type Props = {
  showTeams?: boolean;
  sort?: ResultsTableSort;
};

export function ResultsTableWidget({ showTeams, sort = 'place' }: Props) {
  const router = useRouter();
  const { state } = useTournamentCtx();
  const { tournament, clubId, tournamentId, lastRound } = state;

  const isTournamentOk =
    tournament && ['InProgress', 'Paused', 'Finished'].includes(tournament.status);

  const isOneOnOne = tournament?.system === 'OneOnOne';
  const isSeance = tournament?.system === 'Seance';
  const isKnockout = tournament?.system === 'Knockout';
  const isRoundRobin = tournament?.system === 'RoundRobin';
  const isSwiss = tournament?.system === 'Swiss';
  const isLegacy = isOneOnOne || isSeance || isKnockout;
  const isPrivateTeam = tournament?.mode === 'PrivateTeam';

  const resultsQuery = useTournamentResultsQuery(clubId, tournamentId, isTournamentOk && isLegacy);
  const roundRobinQuery = useRoundRobnResultsQuery(
    clubId,
    tournamentId,
    isTournamentOk && isRoundRobin,
  );
  const swissQuery = useSwissResultsQuery(clubId, tournamentId, isTournamentOk && isSwiss);

  const swissPrivateTeam = useSwissPrivateTeamResultsForTeamsQuery(
    clubId,
    tournamentId,
    isTournamentOk && isSwiss && isPrivateTeam,
  );

  let results: ResultsTableParticipant[] | undefined;

  if (roundRobinQuery) {
    results = roundRobinQuery.data?.results.rows.map((r, i) =>
      mapResultRow(r, i, roundRobinQuery.data.tournament.roundRobinSettings.roundsCount === 2),
    );
  }
  if (isOneOnOne) {
    results = resultsQuery.data?.oneOnOne?.results;
  }
  if (isSwiss) {
    if (isPrivateTeam && showTeams) {
      results = swissPrivateTeam.data?.results.rows.map((r, i) => mapResultRow(r, i));
    } else {
      results = swissQuery.data?.results.rows.map((r, i) => mapResultRow(r, i));
    }
  }

  const sortedResults = useMemo(() => {
    if (!results || !isSwiss || sort === 'place') return results;
    const sorted = [...results];
    sortBy(sorted, (r) => r.seedNumber ?? Infinity);
    sorted.forEach((r, i) => (r.no = i + 1));
    return sorted;
  }, [results, isSwiss, sort]);

  // инвалидация запросов
  const qc = useQueryClient();

  const invalidateResults = useMemo(
    () =>
      debounce(REFETCH_DEBOUNCE_MS, () =>
        qc.invalidateQueries({
          queryKey: QUERY_KEYS.tournamentResultsAll(clubId, tournamentId),
        }),
      ),
    [qc, clubId, tournamentId],
  );

  // Отслеживание изменений игр
  useRoundShortGamesSocket({
    tournamentId,
    roundNumber: lastRound?.roundNumber ?? NaN,
    enabled: lastRound?.status === 'InProgress',
    cb: (e) => {
      if (e.eventType === 'GameChangedShort') {
        const result = e.currentState.result;
        if (result === 'Draw' || result === 'BlackWin' || result === 'WhiteWin') {
          invalidateResults();
        }
      } else if (e.eventType === 'NewGameStarted') {
        invalidateResults();
      }
    },
  });

  // Отслеживание изменений турнира
  // Места становятся известны только после завершения матча, а не после
  // завершения последнего раунда
  // Также догружаем доп. раунды при драфте и финише.
  useUserSocket((e) => {
    switch (e.eventType) {
      case 'TournamentFinished':
      case 'RoundFinished':
      case 'RoundStarted':
      case 'RoundDrafted':
        if (e.tournamentId === tournamentId) {
          invalidateResults();
        }
        break;
    }
  });

  const getGameLink = useCallback(
    (gameId: string) =>
      router.buildLocation({
        to: '/club/$clubId/tournament/$tournamentId/game/$gameId',
        params: { clubId, tournamentId, gameId },
      }).pathname,
    [router, clubId, tournamentId],
  );

  const { preview, onGameMouseEnter, onGameMouseLeave, onRowMouseLeave } = useGamePreview();

  const previewInitialData = useMemo(
    () => (preview?.id ? findGamePreviewData(preview.id, results) : undefined),
    [preview?.id, results],
  );

  const goToGame = useGoToGame(clubId);
  const handleGoToGame = useCallback(
    (gameId: string) => {
      goToGame(gameId, tournamentId);
    },
    [tournamentId, goToGame],
  );

  const skeletonRowCount = showTeams
    ? (tournament?.teamsCount ?? 10)
    : (tournament?.participantsCount ?? 10);
  const skeletonRoundsCount =
    isRoundRobin || isOneOnOne ? skeletonRowCount : (tournament?.rounds.length ?? 5);

  if (isKnockout) {
    const knockoutResults = resultsQuery.data?.knockOutResults;
    if (!knockoutResults) return null;

    return (
      <EliminationBrackets
        className={styles.eliminationBrackets}
        rounds={knockoutResults.rounds}
        onGameClick={handleGoToGame}
        renderShieldQuickPeek={(userId) => <PlayerShieldQuickPeekWidget userId={userId} />}
      />
    );
  }

  if (!sortedResults || !tournament) {
    return (
      <ResultsTableSkeleton
        rowCount={skeletonRowCount}
        roundsCount={skeletonRoundsCount}
        showElo={isSwiss && tournament?.mode === 'Private'}
      />
    );
  }

  return (
    <>
      <ResultsTable
        rows={sortedResults}
        rounds={tournament.rounds}
        system={tournament.system}
        eventFinished={tournament.status === 'Finished'}
        additionalScoresOrder={
          isPrivateTeam && showTeams
            ? tournament.privateTeamModeSettings.tieBreakers || []
            : tournament.additionalScoreOrder
        }
        isRcf={tournament.rcfRankCalculated}
        getGameLink={getGameLink}
        renderShieldQuickPeek={(userId) => <PlayerShieldQuickPeekWidget userId={userId} />}
        onGameMouseEnter={onGameMouseEnter}
        onGameMouseLeave={onGameMouseLeave}
        onRowMouseLeave={onRowMouseLeave}
      />

      <GamePreviewWidget {...preview} initialData={previewInitialData} />
    </>
  );
}

/*
    ____,-------------------------------,____
    \   |           Запчасти            |   /
    /___|-------------------------------|___\
*/

const SORT_TABS: { value: ResultsTableSort; label: string }[] = [
  { value: 'place', label: 'Сортировка по месту' },
  { value: 'seed', label: 'По рейтингу' },
];

export const ResultsTableTabs = memo(function ResultsTableTabs({
  className,
  value,
  onChange,
}: {
  className?: string;
  value: ResultsTableSort;
  onChange: (value: ResultsTableSort) => void;
}) {
  return <Tabs className={className} flat tabs={SORT_TABS} onChange={onChange} value={value} />;
});

/*
    ____,-------------------------------,____
    \   |             Hooks             |   /
    /___|-------------------------------|___\
*/

// eslint-disable-next-line react-refresh/only-export-components
export function useResultsTableSortTabs(show: 'table' | 'teams' | string) {
  const { state } = useTournamentCtx();
  const { tournament } = state;

  const [sort, setSort] = useState<ResultsTableSort>('place');

  const showSortTabs =
    tournament?.system === 'Swiss' &&
    (show === 'table' || show === 'teams') &&
    ['InProgress', 'Finished'].includes(tournament?.status ?? '');

  return { sort, setSort, showSortTabs };
}

/*
    ____,-------------------------------,____
    \   |            Mapping            |   /
    /___|-------------------------------|___\
*/

function mapResultRow(
  r: components['schemas']['ResultRow'],
  i: number,
  isTwoReplays?: boolean,
): ResultsTableParticipant {
  function mapResult(
    result?: components['schemas']['GameResultType'],
  ): GameParticipantsResult | undefined {
    switch (result) {
      case 'Draw':
      case 'Win':
      case 'Loss':
      case 'InProgress':
      case 'Banned':
      case 'NoShow':
      case 'ByeIn':
        return result;
      case 'NoGamePossible':
        return 'Skipped';
      case 'NotDrafted':
      case 'NotStarted':
        return 'UnPlayed';
    }
  }

  function mapGame(c: components['schemas']['ResultCell'], ci: number): ResultsGame {
    return {
      gameId: c.gameId,
      side: c.side,
      opponentSeedNumber: c.opponentSeedNumber ?? undefined,
      resultType: mapResult(c.result),
      roundNo: ci + 1,
      cellResult: c.cellResult,
      score: c.score,
    };
  }

  function mapSubrow(
    s: components['schemas']['ResultSubRow'],
    si: number,
    isTwoReplays?: boolean,
  ): ResultsTableParticipant {
    const cells1Count = isTwoReplays ? s.cells.length / 2 : s.cells.length;
    const cells1 = isTwoReplays ? s.cells.slice(0, cells1Count) : s.cells;
    const cells2 = isTwoReplays ? s.cells.slice(cells1Count) : undefined;
    const isAbsent = !s.partyId;

    return {
      partyId: s.partyId,
      no: si + 1,
      title: isAbsent && s.partyName === '-' ? '—' : s.partyName,
      seedNumber: s.seedNumber,
      games: cells1.map(mapGame),
      games2: cells2?.map(mapGame),
      isAbsent,
    };
  }

  const cells1Count = isTwoReplays ? r.cells.length / 2 : r.cells.length;
  const cells1 = isTwoReplays ? r.cells.slice(0, cells1Count) : r.cells;
  const cells2 = isTwoReplays ? r.cells.slice(cells1Count) : undefined;

  return {
    partyId: r.partyId,
    no: i + 1,
    title: r.partyName,
    place: r.place,
    totalScore: r.totalScore,
    subRows: r.subRows?.map((r, i) => mapSubrow(r, i, isTwoReplays)),
    additionalScores: r.tieBreakerScores,
    games: cells1.map(mapGame),
    games2: cells2?.map(mapGame),
    seedNumber: r.seedNumber,
    elo: r.partyElo,
  } satisfies ResultsTableParticipant;
}

/*
    ____,-------------------------------,____
    \   |             Utils             |   /
    /___|-------------------------------|___\
*/

function findGamePreviewData(
  gameId: string,
  rows?: ResultsTableParticipant[],
): GamePreviewInitialData | undefined {
  if (!rows) return undefined;

  for (const row of rows) {
    const game =
      row.games.find((g) => g.gameId === gameId) ?? row.games2?.find((g) => g.gameId === gameId);

    if (game) {
      if (game.side === 'Unknown') return undefined;

      const participantName = row.title;
      const opponentSeedNumber = game.opponentSeedNumber;
      const opponentRow = opponentSeedNumber
        ? rows.find((r) => r.seedNumber === opponentSeedNumber)
        : undefined;
      const opponentName = opponentRow?.title;

      let winner: ChessWinner | undefined;
      if (game.score === 0.5) {
        winner = 'draw';
      } else if (game.score === 1) {
        winner = game.side === 'White' ? 'white' : 'black';
      } else if (game.score === 0) {
        winner = game.side === 'White' ? 'black' : 'white';
      }

      return {
        whiteName: game.side === 'White' ? participantName : opponentName,
        blackName: game.side === 'Black' ? participantName : opponentName,
        winner,
      };
    }
  }

  return undefined;
}
